import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { Linkedin, Facebook, Instagram, Youtube, ArrowUpRight } from "lucide-react";
import { SiMedium } from "react-icons/si";
import { trackEvent } from "./GAListener";
import { AUDIT_LIST } from "./audits";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SOCIALS = [
  { label: "LinkedIn", href: "https://www.linkedin.com/company/the-digital-marketer-online/", Icon: Linkedin },
  { label: "Facebook", href: "https://www.facebook.com/thedigitalmarketer.online", Icon: Facebook },
  { label: "Instagram", href: "https://www.instagram.com/thedigitalmarketeronline/", Icon: Instagram },
  { label: "Medium", href: "https://medium.com/@thedigitalmarketer.online", Icon: SiMedium },
  { label: "YouTube", href: "https://www.youtube.com/@TheDigitalMarketerOnline", Icon: Youtube },
];

const SERVICE_OPTIONS = [
  "General Inquiry",
  ...AUDIT_LIST.flatMap((a) => a.tiers.map((t) => t.name)),
  "Other",
];

export default function Contact() {
  const [searchParams] = useSearchParams();
  const requestedService = searchParams.get("service");
  const initialService = SERVICE_OPTIONS.includes(requestedService) ? requestedService : "General Inquiry";
  const fromParam = searchParams.get("from") || "";
  const sourcePage = /^\/[A-Za-z0-9\-_/]*$/.test(fromParam) ? fromParam : "/";
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "", service: initialService });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const onServiceChange = (value) => {
    if (value) setForm((f) => ({ ...f, service: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, source_page: sourcePage });
      trackEvent("generate_lead", { method: "contact_form", company: form.company || "(none)", service: form.service });
      toast.success("Message sent. I'll get back to you within 24 hours.");
      setForm({ name: "", email: "", company: "", message: "", service: "General Inquiry" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-14 md:py-20 bg-paper">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-center gap-4 mb-8 md:mb-12">
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-signal">§ 06 — Contact</span>
          <span className="flex-1 h-px bg-line" />
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink2">24-hour reply</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          {/* Left: headline + socials */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7 }}
            className="md:col-span-6"
          >
            <h2 className="font-heading font-black text-6xl md:text-8xl lg:text-9xl leading-[0.85] tracking-tightest">
              Let&apos;s
              <br />
              <span className="italic font-medium">build</span>
              <br />
              pipeline.
              <span className="text-signal">.</span>
            </h2>

            <p className="mt-8 text-base md:text-lg text-ink2 leading-relaxed max-w-lg">
              Ship a campaign, audit a funnel, or architect an entire go-to-market engine. Tell me what
              you&apos;re wrestling with — I&apos;ll reply within 24 hours.
            </p>

            <div className="mt-12 border-t border-ink pt-8">
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink2 mb-5">
                Follow the work
              </div>
              <div className="flex flex-wrap gap-3">
                {SOCIALS.map(({ label, href, Icon }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`social-${label.toLowerCase()}`}
                    className="inline-flex items-center gap-2 border border-ink px-4 py-3 font-heading font-bold text-[12px] uppercase tracking-[0.18em] hover:bg-ink hover:text-paper transition-colors"
                  >
                    <Icon size={14} />
                    {label}
                  </a>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Right: form */}
          <motion.form
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="md:col-span-6 border border-ink p-8 md:p-12"
          >
            <div className="space-y-8">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink2">
                  01 — Name
                </label>
                <input
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  data-testid="contact-input-name"
                  className="brutal-input"
                  placeholder="Your full name"
                />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink2">
                  02 — Email
                </label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  data-testid="contact-input-email"
                  className="brutal-input"
                  placeholder="you@company.com"
                />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink2">
                  03 — Company (optional)
                </label>
                <input
                  name="company"
                  value={form.company}
                  onChange={onChange}
                  data-testid="contact-input-company"
                  className="brutal-input"
                  placeholder="Where do you work?"
                />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink2">
                  04 — What are you interested in?
                </label>
                <Select value={form.service} onValueChange={onServiceChange}>
                  <SelectTrigger
                    data-testid="contact-select-service"
                    className="brutal-input rounded-none border-0 border-b border-ink/20 focus:ring-0 px-0 font-satoshi text-base h-auto data-[placeholder]:text-ink2/50"
                  >
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`service-option-${s.replace(/\s+/g, "-").toLowerCase()}`}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink2">
                  05 — What are you working on?
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  data-testid="contact-input-message"
                  className="brutal-input"
                  placeholder="A project, an audit, a scale problem…"
                  rows={5}
                />
              </div>
            </div>

            <div className="mt-10 flex items-center justify-between gap-4 flex-wrap">
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink2">
                Your data is never shared.
              </span>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="inline-flex items-center gap-3 bg-ink text-paper hover:bg-signal disabled:opacity-60 disabled:cursor-not-allowed px-8 py-4 font-heading font-bold text-[13px] uppercase tracking-[0.2em] transition-colors"
              >
                {submitting ? "Sending…" : "Send Message"} <ArrowUpRight size={16} />
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
